import { ClientId, MAX_TIMER_DURATION_MINUTES } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, trpcClient } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { useInvalidateEntityNotes } from "hooks/use-invalidate-entity-notes";
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { handleEvent } from "util/user-events";

type TimerState = "idle" | "running" | "paused";
export type PendingTimerAction = "start" | "pause" | "resume" | null;

const NOTE_DRAFT_AUTOSAVE_MS = 600;

interface TimerContextValue {
    timerState: TimerState;
    elapsedSeconds: number;
    clientId: ClientId | null;
    clientName: string;
    pendingAction: PendingTimerAction;
    noteDraft: string;
    startTimer: (clientId: ClientId, clientName: string) => Promise<void>;
    pauseTimer: () => Promise<void>;
    resumeTimer: () => Promise<void>;
    resetTimer: () => void;
    saveEntry: () => Promise<void>;
    setNoteDraft: (value: string) => void;
}

const TimerContext = createContext<TimerContextValue | null>(null);

export function formatElapsed(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [timerState, setTimerState] = useState<TimerState>("idle");
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [clientId, setClientId] = useState<ClientId | null>(null);
    const [clientName, setClientName] = useState("");
    const [noteDraft, setNoteDraftState] = useState("");
    const startTimeRef = useRef<Date | null>(null);
    const accumulatedSecondsRef = useRef(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const elapsedSecondsRef = useRef(elapsedSeconds);
    elapsedSecondsRef.current = elapsedSeconds;
    const noteDraftRef = useRef(noteDraft);
    noteDraftRef.current = noteDraft;
    const lastPersistedNoteRef = useRef("");
    const noteDraftSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hydratedRef = useRef(false);
    const inFlightRef = useRef(false);
    const [pendingAction, setPendingAction] = useState<PendingTimerAction>(null);

    const trackTimeFeature = useFeature("trackTime");
    const { settings } = useTenantSettings();
    const isTimeTrackingEnabled = trackTimeFeature && settings.trackTimeEnabled;
    const regionId = useRegionId();

    const invalidateEntityNotes = useInvalidateEntityNotes();
    const queryClient = useQueryClient();
    const trpcHooks = trpc.useTRPC();
    const { data: serverTimer, refetch: refetchServerTimer } = useQuery(
        trpcHooks.activeTimers.getCurrent.queryOptions(
            { regionId: regionId! },
            {
                enabled: isTimeTrackingEnabled && !!regionId,
            },
        ),
    );

    const cancelPendingNoteDraftSync = useCallback(() => {
        if (noteDraftSyncTimeoutRef.current) {
            clearTimeout(noteDraftSyncTimeoutRef.current);
            noteDraftSyncTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (hydratedRef.current) return;
        if (!serverTimer) return;

        hydratedRef.current = true;
        const accumulated = serverTimer.accumulatedSeconds;
        accumulatedSecondsRef.current = accumulated;
        const draft = serverTimer.noteDraft ?? "";
        lastPersistedNoteRef.current = draft;
        setNoteDraftState(draft);

        if (serverTimer.pausedAt) {
            setClientId(serverTimer.clientId);
            setClientName(serverTimer.clientName);
            setElapsedSeconds(accumulated);
            setTimerState("paused");
        } else {
            startTimeRef.current = new Date(serverTimer.startedAt);
            setClientId(serverTimer.clientId);
            setClientName(serverTimer.clientName);
            setTimerState("running");
        }
    }, [serverTimer]);

    const clearInterval_ = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const startInterval = useCallback(
        (start: Date, baseSeconds: number) => {
            clearInterval_();
            intervalRef.current = setInterval(() => {
                const now = new Date();
                const diff = Math.floor((now.getTime() - start.getTime()) / 1000);
                setElapsedSeconds(baseSeconds + diff);
            }, 1000);
        },
        [clearInterval_],
    );

    const startTimer = useCallback(
        async (id: ClientId, name: string) => {
            if (inFlightRef.current) return;
            inFlightRef.current = true;
            setPendingAction("start");
            try {
                await trpcClient.activeTimers.start.mutate({ clientId: id });
                const now = new Date();
                startTimeRef.current = now;
                accumulatedSecondsRef.current = 0;
                cancelPendingNoteDraftSync();
                lastPersistedNoteRef.current = "";
                setNoteDraftState("");
                setClientId(id);
                setClientName(name);
                setElapsedSeconds(0);
                setTimerState("running");
                startInterval(now, 0);
                handleEvent("timer-started");
            } catch {
                await fancyAlert(
                    "Timer error",
                    "Failed to start timer. Please try again.",
                );
            } finally {
                inFlightRef.current = false;
                setPendingAction(null);
            }
        },
        [startInterval, cancelPendingNoteDraftSync],
    );

    const syncFromServer = useCallback(async () => {
        const { data } = await refetchServerTimer();
        clearInterval_();
        if (!data) {
            startTimeRef.current = null;
            accumulatedSecondsRef.current = 0;
            setClientId(null);
            setClientName("");
            setElapsedSeconds(0);
            setTimerState("idle");
            return;
        }
        accumulatedSecondsRef.current = data.accumulatedSeconds;
        setClientId(data.clientId);
        setClientName(data.clientName);
        if (data.pausedAt) {
            startTimeRef.current = null;
            setElapsedSeconds(data.accumulatedSeconds);
            setTimerState("paused");
        } else {
            startTimeRef.current = new Date(data.startedAt);
            setTimerState("running");
        }
    }, [refetchServerTimer, clearInterval_]);

    const pauseTimer = useCallback(async () => {
        if (!regionId) return;
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setPendingAction("pause");
        try {
            const result = await trpcClient.activeTimers.pause.mutate({
                regionId,
            });
            clearInterval_();
            accumulatedSecondsRef.current = result.accumulatedSeconds;
            setElapsedSeconds(result.accumulatedSeconds);
            setTimerState("paused");
            handleEvent("timer-paused");
        } catch {
            await syncFromServer();
            await fancyAlert("Timer error", "Failed to pause timer. Please try again.");
        } finally {
            inFlightRef.current = false;
            setPendingAction(null);
        }
    }, [regionId, clearInterval_, syncFromServer]);

    const resumeTimer = useCallback(async () => {
        if (!regionId) return;
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setPendingAction("resume");
        try {
            const result = await trpcClient.activeTimers.resume.mutate({
                regionId,
            });
            const now = new Date(result.startedAt);
            startTimeRef.current = now;
            accumulatedSecondsRef.current = result.accumulatedSeconds;
            setTimerState("running");
            startInterval(now, result.accumulatedSeconds);
            handleEvent("timer-resumed");
        } catch {
            await syncFromServer();
            await fancyAlert("Timer error", "Failed to resume timer. Please try again.");
        } finally {
            inFlightRef.current = false;
            setPendingAction(null);
        }
    }, [regionId, startInterval, syncFromServer]);

    const teardownTimer = useCallback(() => {
        clearInterval_();
        cancelPendingNoteDraftSync();
        startTimeRef.current = null;
        accumulatedSecondsRef.current = 0;
        lastPersistedNoteRef.current = "";
        setClientId(null);
        setClientName("");
        setElapsedSeconds(0);
        setNoteDraftState("");
        setTimerState("idle");
        if (regionId) {
            trpcClient.activeTimers.stop.mutate({ regionId }).catch(() => {
                fancyAlert(
                    "Timer error",
                    "Failed to clear timer on server. It may reappear on next page load.",
                );
            });
        }
    }, [clearInterval_, cancelPendingNoteDraftSync, regionId]);

    const resetTimer = useCallback(() => {
        teardownTimer();
    }, [teardownTimer]);

    const persistNoteDraft = useCallback(
        (value: string): Promise<void> => {
            if (!regionId) return Promise.resolve();
            if (value === lastPersistedNoteRef.current) return Promise.resolve();
            return trpcClient.activeTimers.updateNoteDraft
                .mutate({ regionId, noteDraft: value })
                .then((): void => {
                    lastPersistedNoteRef.current = value;
                })
                .catch((): void => undefined);
        },
        [regionId],
    );

    const setNoteDraft = useCallback(
        (value: string) => {
            setNoteDraftState(value);
            cancelPendingNoteDraftSync();
            noteDraftSyncTimeoutRef.current = setTimeout(() => {
                noteDraftSyncTimeoutRef.current = null;
                persistNoteDraft(value);
            }, NOTE_DRAFT_AUTOSAVE_MS);
        },
        [cancelPendingNoteDraftSync, persistNoteDraft],
    );

    const saveEntry = useCallback(async () => {
        if (!clientId) return;
        cancelPendingNoteDraftSync();

        const endTime = new Date();
        let liveSeconds = accumulatedSecondsRef.current;
        if (startTimeRef.current && intervalRef.current) {
            liveSeconds += Math.floor(
                (endTime.getTime() - startTimeRef.current.getTime()) / 1000,
            );
        }
        const rawDurationMinutes = Math.max(1, Math.ceil(liveSeconds / 60));
        const durationMinutes = Math.min(MAX_TIMER_DURATION_MINUTES, rawDurationMinutes);
        const startTime = new Date(endTime.getTime() - durationMinutes * 60 * 1000);
        const note = noteDraftRef.current.trim();

        await trpcClient.timeEntries.create.mutate({
            clientId,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            durationMinutes,
            note,
            isManual: false,
        });

        queryClient.invalidateQueries({ queryKey: [["timeEntries"]] });
        invalidateEntityNotes();
        teardownTimer();
    }, [
        clientId,
        teardownTimer,
        invalidateEntityNotes,
        cancelPendingNoteDraftSync,
        queryClient,
    ]);

    useEffect(() => {
        if (timerState === "running" && startTimeRef.current) {
            startInterval(startTimeRef.current, accumulatedSecondsRef.current);
        }
        return clearInterval_;
    }, [timerState, startInterval, clearInterval_]);

    useEffect(() => {
        if (!isTimeTrackingEnabled && timerState !== "idle") {
            cancelPendingNoteDraftSync();
            clearInterval_();
            startTimeRef.current = null;
            accumulatedSecondsRef.current = 0;
            lastPersistedNoteRef.current = "";
            setClientId(null);
            setClientName("");
            setElapsedSeconds(0);
            setNoteDraftState("");
            setTimerState("idle");
        }
    }, [isTimeTrackingEnabled, timerState, clearInterval_, cancelPendingNoteDraftSync]);

    useEffect(() => {
        return () => {
            if (noteDraftSyncTimeoutRef.current) {
                clearTimeout(noteDraftSyncTimeoutRef.current);
            }
        };
    }, []);

    const value = useMemo<TimerContextValue>(
        () => ({
            timerState,
            elapsedSeconds,
            clientId,
            clientName,
            pendingAction,
            noteDraft,
            startTimer,
            pauseTimer,
            resumeTimer,
            resetTimer,
            saveEntry,
            setNoteDraft,
        }),
        [
            timerState,
            elapsedSeconds,
            clientId,
            clientName,
            pendingAction,
            noteDraft,
            startTimer,
            pauseTimer,
            resumeTimer,
            resetTimer,
            saveEntry,
            setNoteDraft,
        ],
    );

    return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

export function useTimer(): TimerContextValue {
    const ctx = useContext(TimerContext);
    if (!ctx) {
        throw new Error("useTimer must be used within a TimerProvider");
    }
    return ctx;
}
